/**
 * Session Manager
 * Centralized service for handling session expiration and re-authentication
 */

import { createFileLogger } from '../utils/logger';

const log = createFileLogger('SESSION_MANAGER');

type SessionExpiredCallback = (context?: SessionContext) => void;

interface SessionContext {
  currentPath: string;
  currentSearch: string;
  message?: string;
}

class SessionManager {
  private listeners: Set<SessionExpiredCallback> = new Set();
  private hasShownModal = false;

  /**
   * Register a callback to be called when session expires
   */
  onSessionExpired(callback: SessionExpiredCallback): () => void {
    this.listeners.add(callback);
    return () => this.listeners.delete(callback);
  }

  /**
   * Trigger session expired state
   * Shows modal only once until resolved
   */
  handleSessionExpired(context?: Partial<SessionContext>): void {
    if (this.hasShownModal) {
      log.warn('Session expired modal already shown, ignoring duplicate trigger');
      return;
    }

    log.warn('🔒 Session expired, triggering handlers');
    this.hasShownModal = true;

    const fullContext: SessionContext = {
      currentPath: window.location.pathname,
      currentSearch: window.location.search,
      message: 'Your session has expired. Please log in again to continue.',
      ...context
    };

    // Store current location for redirect after login
    try {
      localStorage.setItem('redirectAfterLogin', fullContext.currentPath + fullContext.currentSearch);
      log.log('Stored redirect path:', fullContext.currentPath);
    } catch (error) {
      log.error('Failed to store redirect path:', error);
    }

    // Notify all listeners
    this.listeners.forEach(callback => {
      try {
        callback(fullContext);
      } catch (error) {
        log.error('Error in session expired callback:', error);
      }
    });
  }

  /**
   * Reset the session manager after successful re-authentication
   */
  reset(): void {
    log.log('Session manager reset - user re-authenticated');
    this.hasShownModal = false;
  }

  /**
   * Check if token is expired or about to expire
   * Returns true if token is expired or will expire within the next minute
   */
  isTokenExpired(token: string): boolean {
    try {
      const { parseJwtPayload } = require('../utils/jwtUtils');
      const payload = parseJwtPayload(token);
      
      if (!payload || !payload.exp) {
        return true;
      }

      const now = Date.now();
      const expiry = payload.exp * 1000;
      const bufferMs = 60 * 1000; // 1 minute buffer

      return expiry - now < bufferMs;
    } catch (error) {
      log.error('Error checking token expiration:', error);
      return true;
    }
  }

  /**
   * Get time until token expires (in ms)
   */
  getTimeUntilExpiry(token: string): number | null {
    try {
      const { parseJwtPayload } = require('../utils/jwtUtils');
      const payload = parseJwtPayload(token);
      
      if (!payload || !payload.exp) {
        return null;
      }

      const now = Date.now();
      const expiry = payload.exp * 1000;
      
      return Math.max(0, expiry - now);
    } catch (error) {
      log.error('Error calculating time until expiry:', error);
      return null;
    }
  }
}

export const sessionManager = new SessionManager();
export default sessionManager;


